import {
  MutationV1,
  MutatorDefs,
  PullResponseV1,
  Replicache,
  dropAllDatabases,
  dropDatabase,
} from 'replicache'

import { pusherReceiver } from './poke'
import { invariant } from '@epic-web/invariant'
import type { WebhookArgsForMutator } from '@basehub/data/replicache/mutators/shared-types'
import { checkHasSameKeys } from '~/lib/check-has-same-keys'
import { create } from 'zustand'
import { Compressed, decompress } from 'compress-json'
import { transactionClientContext } from '@basehub/data/replicache/tx/iso'
import type { ClientOnlyParams } from '@basehub/data/replicache/tx/spec'
import type { SyncerSubsMapItem } from '@basehub/data/replicache/syncers'
import { useSyncerSubs } from './use-custom-syncer-sub'
import { isObjectAsWeCommonlyCallIt } from '@basehub/utils/object'
import { getURL } from '@basehub/shared-constants'
import { getClerkSessionFromCookie } from './get-clerk-session-from-cookie'
// import { checkHasSameKeys } from '~/lib/check-has-same-keys'
// import { findWithIndex } from '~/lib/find-with-index'

const DEBUG = false

export type PushQueryParams = {
  branchOrCommit: string | null
}

export type PullQueryParams = {
  branchOrCommit: string | null
  requestCommitRootBlockIds: string[] | undefined
}

export const useReplicacheParamsStore = create<{
  team: undefined
  repo: {
    branchOrCommit: string | undefined
    requestCommitRootBlockIds: string[] | undefined
    playgroundId: string | undefined
  }
}>(() => {
  return {
    team: undefined,
    repo: {
      branchOrCommit: undefined,
      requestCommitRootBlockIds: undefined,
      playgroundId: undefined,
    },
  }
})

export async function initReplicache<M extends MutatorDefs>(config: {
  spaceID: string
  userId: string
  mutators: M
  type: 'team' | 'repo'
  noPoke?: boolean
  clientOnly: Omit<
    ClientOnlyParams,
    'spaceID' | 'passedFirstMutatorCheck' | 'userId'
  >
}) {
  const replicache = new Replicache({
    mutators: config.mutators,
    name: `${config.userId}-${config.spaceID}${config.clientOnly.playgroundId ? `-${config.clientOnly.playgroundId}` : ''}`,
    logLevel: DEBUG
      ? // if using chrome (duh), make sure you
        // select the "verbose" console log level
        // else you won't see these logs
        'debug'
      : 'error',
    indexes:
      config.type === 'repo'
        ? {
            brsBySourceBlockId: {
              jsonPointer: '/sourceBlockId',
              prefix: 'br/',
            },
            brsByBlockId: {
              jsonPointer: '/blockId',
              prefix: 'br/',
            },
            brsByParentId: {
              jsonPointer: '/parentId',
              prefix: 'br/',
            },
            brsByBranchId: {
              jsonPointer: '/branchId',
              prefix: 'br/',
            },
            milestonesBySourceBlockId: {
              jsonPointer: '/sourceBlockId',
              prefix: 'milestone/',
            },
          }
        : undefined,
    pusher: async (requestBody) => {
      invariant(
        requestBody.pushVersion === 1,
        `replicache push version was expected to be 1, found ${requestBody.pushVersion}`
      )

      const dedupedMutations: Array<MutationV1> = []

      for (let index = 0; index < requestBody.mutations.length; index++) {
        const mutation = requestBody.mutations[index]
        if (!mutation) continue

        /**
         * we'll check if "should override" just with the previous mutation, not before that.
         * why? because mutation order is important, and we don't want to mess that up. imagine:
         * 1. updateBlock
         * 2. reorderBlock
         * 3. updateBlock
         * we don't want #3 to override #1 as the one that came in the middle _might_ depend on #1 somehow (remember we have references, components, etc).
         * if we only ever check for previous, we won't mess up complex stuff, and we'll still be able to dedupe long sequences of overridable mutations.
         */
        const prevMutationIndex = findValidPrevMutationIndex({
          current: mutation,
          mutations: dedupedMutations,
        })
        const prevMutation = dedupedMutations[prevMutationIndex]
        if (prevMutation && prevMutation.clientID === mutation.clientID) {
          // see if the mutation should be overriden
          if (shouldOverrideMutation(prevMutation, mutation)) {
            // put "air" in deduped mutation
            dedupedMutations[prevMutationIndex] = {
              ...prevMutation,
              args: {},
              name: '__deduped',
            }
          }
        }

        dedupedMutations.push(mutation)
      }

      const maxPayloadSize = 1024 * 1024 * 3.5 // 3.5MB

      const batches = dedupedMutations.reduce<
        Array<{ currentSize: number; mutations: MutationV1[] }>
      >(
        (acc, mutation) => {
          const mutationBytesSize = new Blob([JSON.stringify(mutation.args)])
            .size
          const currentBatch = acc[acc.length - 1]
          invariant(currentBatch, 'current branch was not found')

          if (mutationBytesSize >= maxPayloadSize) {
            console.warn(
              `Mutation ${mutation.name} is too large, skipping.`,
              mutationBytesSize,
              maxPayloadSize
            )
            currentBatch.mutations.push({
              ...mutation,
              args: {},
              name: '__deduped',
            })
            return acc
          }

          const shouldAddToCurrentBatch =
            currentBatch.currentSize === 0 ||
            currentBatch.currentSize + mutationBytesSize < maxPayloadSize
          if (shouldAddToCurrentBatch) {
            currentBatch.mutations.push(mutation)
            currentBatch.currentSize += mutationBytesSize
          } else {
            // create new batch
            acc.push({
              currentSize: mutationBytesSize,
              mutations: [mutation],
            })
          }

          return acc
        },
        [{ currentSize: 0, mutations: [] }]
      )

      const requests = await Promise.all(
        batches.map(async (batch) => {
          if (batch.mutations.length === 0) return
          const resp = await fetch(
            getReplicacheURL({
              op: 'push',
              spaceID: config.spaceID,
              ...useReplicacheParamsStore.getState()[config.type],
            }),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-basehub-session': getClerkSessionFromCookie() ?? '',
              },
              body: JSON.stringify({
                ...requestBody,
                mutations: batch.mutations,
              }),
            }
          )

          return resp
        })
      )

      const erroredRequest = requests.find(
        (r) => Boolean(r?.status.toString().startsWith('2')) === false
      )

      return {
        httpRequestInfo: {
          httpStatusCode: erroredRequest ? erroredRequest.status : 200,
          errorMessage: erroredRequest ? erroredRequest.statusText : '',
        },
      }
    },
    puller: async (requestBody) => {
      try {
        const pullURL = getReplicacheURL({
          op: 'pull',
          spaceID: config.spaceID,
          ...useReplicacheParamsStore.getState()[config.type],
        })

        const syncerSubs: Record<
          string,
          Array<SyncerSubsMapItem['params']>
        > = {}
        const { syncerSubs: storedSyncerSubs } = useSyncerSubs.getState()
        const includedQueriesHashes = new Set<string>()
        for (const {
          clientId,
          data,
          queryKeyHash,
        } of storedSyncerSubs.values()) {
          if (clientId !== replicache.clientID) continue
          if (includedQueriesHashes.has(queryKeyHash)) continue
          includedQueriesHashes.add(queryKeyHash)
          syncerSubs[data.syncer] ??= []
          syncerSubs[data.syncer]?.push(
            isObjectAsWeCommonlyCallIt(data.params)
              ? { ...data.params, __queryKeyHash: queryKeyHash }
              : data.params
          )
        }

        const resp = await fetch(pullURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-basehub-session': getClerkSessionFromCookie() || '',
          },
          body: JSON.stringify({ ...requestBody, syncerSubs }),
        })

        const erroredRequest =
          Boolean(resp.status.toString().startsWith('2')) === false

        const response = (await resp.json()) as PullResponseV1 & {
          isCompressed: boolean
          error: string
        }
        if ('patch' in response && response.isCompressed) {
          response.patch = decompress(response.patch as unknown as Compressed)
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.log('patch', response.patch)
          }
        }

        if (response.error === 'ClientStateNotFound') {
          await dropDatabase(replicache.idbName)
          window.location.reload()
          throw new Error('ClientStateNotFound')
        }

        if (response.error === 'VersionNotSupported') {
          const { errors } = await dropAllDatabases()
          if (errors.length > 0) {
            console.error('errors while dropping databases', errors)
          }
          window.location.reload()
          throw new Error('VersionNotSupported')
        }

        return {
          httpRequestInfo: {
            httpStatusCode: resp.status,
            errorMessage: erroredRequest ? resp.statusText : '',
          },
          response,
        }
      } catch (e) {
        console.error(e)
        throw e
      }
    },
  })

  if (config.noPoke) {
    return { replicache, pokeChannel: undefined }
  }

  transactionClientContext.setState((old) => {
    old.set(replicache.clientID, {
      spaceID: config.spaceID,
      passedFirstMutatorCheck: false,
      userId: config.userId,
      ...config.clientOnly,
    })
    return old
  })

  // Replicache uses an empty "poke" mesmauve sent over some pubsub channel
  // to know when to pull changes from the server. There are many ways to
  // implement pokes. This sample app implements two different ways.
  // By default, we use Server-Sent Events. This is simple, cheap, and fast,
  // but requires a stateful server to keep the SSE channels open. For
  // serverless platforms we also support pokes via Supabase. See:
  // - https://doc.replicache.dev/deploy
  // - https://doc.replicache.dev/how-it-works#poke-optional
  // - https://github.com/supabase/realtime
  // - https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events
  const pokeChannel = pusherReceiver(config.spaceID, async () => {
    replicache.pull()
  })

  return { replicache, pokeChannel }
}

export const getReplicacheURL = ({
  op,
  spaceID,
  branchOrCommit,
  playgroundId,
  requestCommitRootBlockIds,
}: {
  op: 'push' | 'pull'
  spaceID: string
  branchOrCommit?: string
  playgroundId?: string
  requestCommitRootBlockIds?: string[]
}) => {
  let query = `spaceID=${spaceID}`
  if (branchOrCommit) {
    query += `&branchOrCommit=${branchOrCommit}`
  }
  if (playgroundId) {
    query += `&playgroundId=${playgroundId}`
  }
  if (requestCommitRootBlockIds?.length) {
    query += `&requestCommitRootBlockIds=${requestCommitRootBlockIds.join(',')}`
  }

  return (
    getURL(
      'pump',
      process.env.NEXT_PUBLIC_ENVIRONMENT as 'production' | undefined
    ) + `/replicache/${op}?${query}`
  )
}

function shouldOverrideMutation(prev: MutationV1, curr: MutationV1) {
  if (prev.name !== curr.name) return false
  switch (prev.name) {
    case 'keepClientAlive': {
      const parsedPrevArgs =
        prev.args as WebhookArgsForMutator<'keepClientAlive'>['mutationInput']
      const parsedCurrArgs =
        curr.args as WebhookArgsForMutator<'keepClientAlive'>['mutationInput']

      if (parsedPrevArgs.id === parsedCurrArgs.id) {
        return true
      } else {
        return false
      }
    }
    case 'updateBlock': {
      const parsedPrevArgs = prev.args as any as Omit<
        WebhookArgsForMutator<'updateBlock'>['mutationInput'],
        'serverOnly'
      >
      const parsedCurrArgs = curr.args as any as Omit<
        WebhookArgsForMutator<'updateBlock'>['mutationInput'],
        'serverOnly'
      >

      // strict equals
      if (
        parsedPrevArgs.id !== parsedCurrArgs.id ||
        parsedPrevArgs.path !== parsedCurrArgs.path ||
        parsedPrevArgs.strategy !== parsedCurrArgs.strategy
      ) {
        return false
      }

      // compare by keys
      const hasSameKeys = checkHasSameKeys(
        parsedPrevArgs.data,
        parsedCurrArgs.data,
        { deep: parsedCurrArgs.strategy === 'merge-deep' }
      )

      return hasSameKeys
    }
    case 'updateClientFocus': {
      const parsedPrevArgs =
        prev.args as WebhookArgsForMutator<'updateClientFocus'>['mutationInput']
      const parsedCurrArgs =
        curr.args as WebhookArgsForMutator<'updateClientFocus'>['mutationInput']
      return (
        parsedPrevArgs.id === parsedCurrArgs.id &&
        parsedPrevArgs.branchId === parsedCurrArgs.branchId &&
        parsedPrevArgs.selection?.blockId === parsedCurrArgs.selection?.blockId
      )
    }
    default:
      break
  }
  return false
}

function findValidPrevMutationIndex({
  current,
  mutations,
}: {
  current: MutationV1
  mutations: MutationV1[]
}) {
  /**
   * In most cases, prevMutation will be the last in the dedupedMutations array.
   * However, if the current mutation is an updateBlock, and in particular a rich-text block,
   * the sequence might be something like:
   * - updateClientFocus
   * - updateBlock
   * - updateClientFocus
   * - updateBlock
   * And in that case, if we don't successfully __dedupe, then mutations might get stuck / "request entity too large".
   */

  let prevMutationIndex = mutations.length - 1
  let found = false

  while (!found && prevMutationIndex >= 0) {
    const currentPrev = mutations[prevMutationIndex]

    if (currentPrev && currentPrev.name === '__deduped') {
      prevMutationIndex--
      continue
    } else if (current.name === 'updateBlock') {
      const currentArgs =
        current.args as any as WebhookArgsForMutator<'updateBlock'>['mutationInput']
      if (currentPrev && currentPrev.name === 'updateClientFocus') {
        const args =
          currentPrev.args as WebhookArgsForMutator<'updateClientFocus'>['mutationInput']
        if (args.selection?.blockId === currentArgs.id) {
          // skip this one
          prevMutationIndex--
          continue
        }
      }
    } else if (current.name === 'updateClientFocus') {
      // likewise in the inverse case
      const currentArgs =
        current.args as WebhookArgsForMutator<'updateClientFocus'>['mutationInput']
      if (currentPrev && currentPrev.name === 'updateBlock') {
        const args =
          currentPrev.args as any as WebhookArgsForMutator<'updateBlock'>['mutationInput']
        if (args.id === currentArgs.selection?.blockId) {
          // skip this one
          prevMutationIndex--
          continue
        }
      }
    }

    // else
    found = true
  }

  return prevMutationIndex
}
